import type { Projection, ScreenPos, WorldPos } from "./Projection";

/**
 * Oblique / ¾ JRPG view (Stardew, Pokémon, RPG Maker).
 *
 * Mathematically identical to top-down for movement, click→world, and tile
 * placement. The defining oblique trait — props with visible front faces
 * (taller-than-floor wall sprites) — is an art + renderer concern, not a
 * projection concern. Until the renderer supports per-tile sprite heights,
 * oblique zones use the same tile placement as top-down; the visual
 * difference comes from the art set.
 */

export const OBLIQUE_TILE = 32;

export class ObliqueProjection implements Projection {
  readonly kind = "oblique" as const;
  readonly tileWidth = OBLIQUE_TILE;
  readonly tileHeight = OBLIQUE_TILE;

  worldToScreen(x: number, y: number): ScreenPos {
    return { sx: x * OBLIQUE_TILE, sy: y * OBLIQUE_TILE };
  }

  tileFoot(x: number, y: number): ScreenPos {
    return { sx: x * OBLIQUE_TILE + OBLIQUE_TILE / 2, sy: y * OBLIQUE_TILE + OBLIQUE_TILE };
  }

  screenToWorld(sx: number, sy: number): WorldPos {
    return { x: sx / OBLIQUE_TILE, y: sy / OBLIQUE_TILE };
  }

  defaultOrigin(viewportW: number, viewportH: number, gridSize: number): ScreenPos {
    const middle = this.worldToScreen(gridSize / 2, gridSize / 2);
    return {
      sx: viewportW / 2 - middle.sx,
      sy: viewportH / 2 - middle.sy,
    };
  }

  /** Y-sort so props in lower rows occlude characters in upper rows. */
  depthKey(_x: number, y: number): number {
    return y * 1000;
  }

  rotateInputToWorld(rx: number, ry: number): { dx: number; dy: number } {
    return { dx: rx, dy: ry };
  }

  worldDirToFrame(dir: number): number {
    return ((dir % 8) + 8) % 8;
  }
}
